import type { OnInit } from '@angular/core'
import { Directive, inject, NgZone, ViewChild, ViewContainerRef } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { FullStory, init } from '@fullstory/browser'
import { of } from 'rxjs'
import { filter, switchMap, take } from 'rxjs/operators'

import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { ScriptLoaderService } from '@app/modules/http/services/script-loader.service'
import { PwaService } from '@app/modules/pwa/services/pwa.service'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import { LayoutService } from '@app/services/layout.service'
import { SiteStore } from '@app/store/site.store'

import { removeNextPrefix } from './core/remove-next-prefix'
import { PageViewAnalyticsService } from './modules/analytics/page-view-analytics.service'
import { MetaService } from './modules/meta/meta.service'
import { PopupService } from './modules/popup/popup.service'
import { PushNotificationsService } from './modules/push-notifications/services/push-notifications.service'
import { ModalService } from './overlays/modal/modal.service'
import { ModalUpdateInvalidEmailComponent } from './overlays/modal/update-invalid-email/update-invalid-email.component'
import { MemberService } from './services/member.service'
import { OrwellService } from './services/orwell.service'
import { OverlaysService } from './services/overlays.service'
import { RouteHistoryService } from './services/route-history.service'
import { RoutingService } from './services/routing.service'
import { ScrollBlockUiService } from './services/scroll-block-ui.service'
import { TransitionService } from './services/transition.service'
import { UserAgentService } from './services/user-agent.service'
import { AttributionService } from './modules/analytics/attribution.service'

@Directive()
export class AppComponentBase implements OnInit {
  private readonly creditsStore = inject(CreditsStore)
  readonly memberService = inject(MemberService)
  private readonly viewContainerRef = inject(ViewContainerRef)
  private readonly overlaysService = inject(OverlaysService)
  private readonly modalService = inject(ModalService)
  private readonly orwellService = inject(OrwellService)
  private readonly ngZone = inject(NgZone)
  private readonly pushNotificationsService = inject(PushNotificationsService)
  private readonly pwaService = inject(PwaService)
  private readonly layoutService = inject(LayoutService)
  private readonly metaService = inject(MetaService)
  private readonly popupService = inject(PopupService)
  private readonly router = inject(Router)
  private readonly scriptLoader = inject(ScriptLoaderService)
  private readonly siteStore = inject(SiteStore)
  readonly attributionService = inject(AttributionService) // initializes on create

  readonly layoutConfig$ = this.layoutService.config$

  @ViewChild('content', { static: false, read: ViewContainerRef }) set content(ref: ViewContainerRef) {
    if (!ref) {
      return
    }

    this.overlaysService.init(this.viewContainerRef, ref)
  }

  constructor() {
    const transitionService = inject(TransitionService)
    const routeHistoryService = inject(RouteHistoryService)
    const scrollBlockUiService = inject(ScrollBlockUiService)
    const routingService = inject(RoutingService)
    const userAgentService = inject(UserAgentService)

    // inject acts as a service initialization
    inject(PageViewAnalyticsService)

    scrollBlockUiService.init()
    this.metaService.init()
    this.popupService.init()
    routeHistoryService.startListening()
    transitionService.onStart({}, ([, to]) => {
      const url = removeNextPrefix(to.url)
      if (!url.startsWith('/quick-start') && !url.startsWith('/signup')) {
        void this.orwellService.submit()
      }
    })
    routingService.historyChange$.subscribe(() => this.memberService.resetPage())
    void userAgentService.init()
  }

  ngOnInit(): void {
    void this.memberService.onPageLoad()
    void this.creditsStore.init()
    void this.pwaService.init()
    this.pushNotificationsService.init()

    this.memberService.account$.pipe(take(1)).subscribe(account => {
      if (account.fullstory_org) {
        this.ngZone.runOutsideAngular(() => {
          init({ orgId: account.fullstory_org })
          FullStory('setIdentity', `${account.user_id}`)
        })
      }

      this.ngZone.runOutsideAngular(() => {
        if (this.siteStore.features().usr) {
          void this.scriptLoader.load('/api/usr.js').catch(error => ErrorHandlers.handleError(error))
        }
      })
      this.checkForEmailInvalid(account.email)
    })
  }

  private checkForEmailInvalid(email: string): void {
    this.popupService.askForShowing('invalid-email', () => {
      const { instance, componentRef } = this.modalService.create(ModalUpdateInvalidEmailComponent, {
        showCloseButton: false,
        closeOnBackdropClick: false,
      })
      instance.email = email

      return {
        componentRef,
        lockQueueUntil: instance.decision$.pipe(
          switchMap(confirmed => {
            return confirmed
              ? this.router.events.pipe(
                  filter(event => event instanceof NavigationEnd),
                  filter((event: NavigationEnd) => !event.url.startsWith('/next/settings/email')),
                )
              : of(0)
          }),
        ),
      }
    })
  }
}
